// @ts-nocheck
/*
  EOY 2026 giftbox showcase — sections/pb-eoy-giftbox-showcase.liquid
  - Tabs (WAI-ARIA tab pattern, automatic activation): switch card + section
    background, reset the eye toggle, scroll the active tab into view.
  - Eye button: box image <-> revealed (contents) image.
  - "+ Toevoegen": PBCart.addLine; the unified drawer opens itself on
    pb:cart:updated (source addLine). On failure the button shows an error
    for ERROR_MS (and the status region announces it), then resets.
  - Sparkles: the shared <pb-eoy-sparkles> layer (assets/pb-eoy-sparkles.js).
  - Coming soon: Liquid marks a panel `is-upcoming` when its going_live_date is
    after today. A cached page can outlive that date, so the date is checked
    again here (Europe/Brussels) and the class removed on/after the day.
*/
(() => {
  if (customElements.get('pb-eoy-giftbox-showcase')) return;

  const ERROR_MS = 3500;
  const ERROR_TEXT = 'Niet gelukt, probeer opnieuw';
  const SHOP_TIME_ZONE = 'Europe/Brussels';

  // Today as YYYY-MM-DD in the shop's time zone (en-CA formats dates that way).
  function todayYmd() {
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone: SHOP_TIME_ZONE }).format(new Date());
    } catch {
      return new Date().toISOString().slice(0, 10);
    }
  }

  async function waitForPBCart(tries = 80, delay = 50) {
    for (let i = 0; i < tries; i++) {
      if (window.PBCart && typeof window.PBCart.addLine === 'function') return window.PBCart;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
    throw new Error('PBCart.addLine is niet beschikbaar');
  }

  class PbEoyGiftboxShowcase extends HTMLElement {
    connectedCallback() {
      this.tablist = this.querySelector('[data-gbs-tablist]');
      this.tabs = Array.from(this.querySelectorAll('[data-gbs-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-gbs-panel]'));
      this.releaseLivePanels();

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.addEventListener('click', this.onClick);
      this.tablist?.addEventListener('keydown', this.onKeydown);

      const active = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      this.scrollTabIntoView(Math.max(0, active), false);
    }

    // A panel rendered as "coming soon" whose date has arrived goes live.
    releaseLivePanels() {
      const today = todayYmd();
      this.panels.forEach((panel) => {
        const liveDate = panel.dataset.gbsLiveDate;
        if (liveDate && liveDate <= today) panel.classList.remove('is-upcoming');
      });
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.tablist?.removeEventListener('keydown', this.onKeydown);
    }

    onClick(event) {
      const tab = event.target.closest('[data-gbs-tab]');
      if (tab && this.contains(tab)) {
        this.select(this.tabs.indexOf(tab));
        return;
      }

      const eye = event.target.closest('[data-gbs-eye]');
      if (eye && this.contains(eye)) {
        this.toggleContents(eye);
        return;
      }

      const atc = event.target.closest('[data-gbs-atc]');
      if (atc && this.contains(atc)) this.addToCart(atc);
    }

    onKeydown(event) {
      const current = this.tabs.indexOf(document.activeElement);
      if (current === -1) return;

      let next = null;
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = (current + 1) % this.tabs.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = (current - 1 + this.tabs.length) % this.tabs.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = this.tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.select(next);
      this.tabs[next].focus({ preventScroll: true });
    }

    select(index) {
      if (index < 0 || index >= this.tabs.length) return;

      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });

      this.panels.forEach((panel, i) => {
        panel.hidden = i !== index;
        // Every switch starts on the closed box again.
        const eye = panel.querySelector('[data-gbs-eye]');
        if (eye) this.toggleContents(eye, false);
      });

      const bg = this.tabs[index].dataset.bg;
      if (bg) this.style.setProperty('--pb-eoy-gbs-bg', bg);

      this.scrollTabIntoView(index, true);
    }

    // Horizontal scroll of the tab row only — scrollIntoView would also move the page.
    scrollTabIntoView(index, smooth) {
      const list = this.tablist;
      const tab = this.tabs[index];
      if (!list || !tab || list.scrollWidth <= list.clientWidth) return;

      const listRect = list.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      const left = list.scrollLeft + (tabRect.left - listRect.left) - (listRect.width - tabRect.width) / 2;
      list.scrollTo({ left: Math.max(0, left), behavior: smooth ? 'smooth' : 'auto' });
    }

    toggleContents(eye, force) {
      const media = eye.closest('[data-gbs-media]');
      if (!media) return;

      const show = typeof force === 'boolean' ? force : !media.classList.contains('is-contents');
      media.classList.toggle('is-contents', show);
      eye.setAttribute('aria-pressed', String(show));
      eye.setAttribute('aria-label', show ? 'Bekijk de doos' : 'Bekijk de inhoud');
    }

    async addToCart(button) {
      const variantId = button.dataset.variantId;
      if (!variantId || button.disabled || button.getAttribute('aria-busy') === 'true') return;

      this.clearError(button);
      button.setAttribute('aria-busy', 'true');
      try {
        const cart = await waitForPBCart();
        await cart.addLine(variantId, 1);
      } catch (error) {
        window.PBDebug?.log('[pb-eoy-giftbox-showcase] addLine failed', error);
        console.error(error);
        this.showError(button);
      } finally {
        button.removeAttribute('aria-busy');
      }
    }

    showError(button) {
      button.dataset.label ??= button.textContent;
      button.textContent = ERROR_TEXT;
      button.classList.add('is-error');
      const status = button.parentElement?.querySelector('[data-gbs-atc-status]');
      if (status) status.textContent = ERROR_TEXT;
      button.errorTimer = setTimeout(() => this.clearError(button), ERROR_MS);
    }

    clearError(button) {
      clearTimeout(button.errorTimer);
      if (!button.classList.contains('is-error')) return;
      button.classList.remove('is-error');
      button.textContent = button.dataset.label;
      const status = button.parentElement?.querySelector('[data-gbs-atc-status]');
      if (status) status.textContent = '';
    }
  }

  customElements.define('pb-eoy-giftbox-showcase', PbEoyGiftboxShowcase);
})();
